<template>
    <div class="sms-input-card">
        <div class="form-section">
            <div class="field">
                <label class="field-label">
                    <i class="material-icons">person</i>
                    {{ $t("message.recipient") }}
                </label>
                <div class="phone-row">
                    <vue-country-code
                        class="country-code"
                        :enabledCountryCode="true"
                        @onSelect="onSelect($event)"
                        defaultCountry="TR"
                    />
                    <a-input
                        size="large"
                        v-model="number"
                        :placeholder="$t('message.phone')"
                        autocomplete="off"
                        trim
                        type="text"
                        v-mask="'############'"
                    />
                </div>
            </div>

            <div class="field">
                <label class="field-label">
                    <i class="material-icons">chat</i>
                    {{ $t("message.messageContent") }}
                </label>
                <div class="message-box">
                    <TiptapEditor
                        :placeholder="$t('message.writeYourMessage')"
                        :show-attributes="false"
                        :show-emoji="true"
                        v-model="text"
                        :preview-mode="false"
                        :whatsapp-mode="false"
                        :allowed-attributes="false"
                        :max-character-count="160"
                    />
                    <div
                        class="char-counter"
                        :class="{ warn: charCount > 140 && charCount <= 160, over: charCount > 160 }"
                    >
                        {{ charCount }} / 160
                    </div>
                </div>
            </div>
        </div>

        <div class="footer">
            <div class="credit-badge" v-if="credit !== undefined && credit !== null">
                <i class="material-icons">account_balance_wallet</i>
                <span class="credit-label">{{ $t("message.remainingCredit") }}:</span>
                <span class="credit-value">{{ credit }}</span>
            </div>
            <a-button
                type="primary"
                size="large"
                :disabled="!formIsValid()"
                class="send-button"
                @click="sendSMS"
            >
                <i class="material-icons">send</i>
                {{ $t("message.sendSms") }}
            </a-button>
        </div>
    </div>
</template>
<script>
import Vue from "vue";
import VueCountryCode from "vue-country-code";
import TiptapEditor from "../../../../Chatbot/pages/dashboard/common/TiptapEditor.vue";

export default Vue.extend({
    name: "PostaGuverciniSendSmsInput",
    components: { VueCountryCode, TiptapEditor },
    props: ["channel", "credit"],
    data() {
        return {
            text: "",
            number: "",
            countryCode: "",
        };
    },
    computed: {
        charCount() {
            return String(this.text || "").replace(/<[^>]*>/g, "").length;
        },
    },
    methods: {
        onSelect({ dialCode }) {
            this.countryCode = dialCode;
        },
        formIsValid() {
            if (this.charCount < 1 || this.charCount > 160) return false;
            if (!this.number) return false;
            return true;
        },
        sendSMS() {
            if (typeof this.credit === "number" && this.credit <= 0) {
                this.$message.error(this.$t("message.notEnoughCreditToSendSms"));
                return;
            }
            this.$emit("sendSMS", { text: this.text, number: this.number });
            this.$message.success(this.$t("message.smsSentSuccessfully"));
        },
    },
});
</script>

<style scoped lang="scss">
.sms-input-card {
    background: var(--color-bg-default);
    display: flex;
    flex-direction: column;
    border: 1px solid var(--color-border-default);
    border-radius: 12px;
    padding: 24px;
    max-width: 560px;
    width: 100%;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);

    .form-section {
        display: flex;
        flex-direction: column;
        gap: 18px;
    }

    .field {
        display: flex;
        flex-direction: column;
        gap: 8px;

        .field-label {
            display: inline-flex;
            align-items: center;
            gap: 6px;
            font-size: 13px;
            font-weight: 600;
            color: var(--color-text-strong-default);
            margin: 0;

            i.material-icons {
                font-size: 16px;
                color: var(--color-text-subtle-default);
            }
        }
    }

    .phone-row {
        display: flex;
        gap: 8px;
        align-items: stretch;

        .country-code {
            flex-shrink: 0;
        }

        .ant-input {
            flex: 1;
        }
    }

    .message-box {
        position: relative;
        border: 1px solid var(--color-border-default);
        border-radius: 8px;
        padding: 12px 12px 32px;
        background: var(--color-bg-subtle-default);
        transition: border-color 0.15s ease;
        min-height: 120px;

        &:focus-within {
            border-color: var(--color-bg-primary-default);
        }

        .char-counter {
            position: absolute;
            right: 10px;
            bottom: 8px;
            font-size: 12px;
            color: var(--color-text-subtle-default);

            &.warn {
                color: #faad14;
            }

            &.over {
                color: #f5222d;
                font-weight: 600;
            }
        }
    }

    .footer {
        margin-top: 24px;
        padding-top: 18px;
        border-top: 1px solid var(--color-border-default);
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 12px;
        flex-wrap: wrap;

        .credit-badge {
            display: inline-flex;
            align-items: center;
            gap: 6px;
            padding: 6px 12px;
            background: var(--color-bg-subtle-default);
            border: 1px solid var(--color-border-default);
            border-radius: 20px;
            font-size: 13px;

            i.material-icons {
                font-size: 16px;
                color: var(--color-bg-primary-default);
            }

            .credit-label {
                color: var(--color-text-subtle-default);
            }

            .credit-value {
                font-weight: 700;
                color: var(--color-text-strong-default);
            }
        }

        .send-button {
            display: inline-flex;
            align-items: center;
            gap: 6px;
            height: 40px;
            padding: 0 20px;
            border-radius: 8px;
            font-weight: 600;
            margin-left: auto;

            i.material-icons {
                font-size: 18px;
            }
        }
    }
}
</style>
